import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { DealershipChooser } from 'components';
import {
  Form,
  Select,
  InputNumber,
  Button,
  Card,
  Col,
  Row,
  Spin,
  notification,
} from 'antd';
import styled from 'styled-components';
import { observe } from 'mobx';
import { ShopStore } from 'stores';
import { get } from 'lodash';
import dayjs from 'dayjs';
import { FiltersFormWithVVA } from 'components/FleetReports/FiltersFormWithVVA';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { downloadCSV } from 'shared/utils';
import { notifications } from '../../helpers/notificationHelper';
import { ExportOutlined } from '@ant-design/icons';

const { Option } = Select;

const H2Styled = styled.h2`
  font-size: 1.2rem;
`;

class PartRepeatVisitsPage extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    match: PropTypes.object,
  };

  state = {
    isLoadingPartRepeatVisits: false,
    isLoadingReasonsForVisits: false,
    rawData: [],
    dataForReasonsForVisits: [],
    dataForPartRepeatVisits: [],
    limit: 20,
    dateRange: [dayjs().subtract(30, 'day'), dayjs()],
    filter: {
      vendor: [],
      make: [],
      model: [],
      year: [],
      carIds: [],
      assetType: [],
      VMRSSysCode: [],
    },
  };

  componentDidMount() {
    this.init();
  }

  componentWillUnmount() {
    this.disposer();
  }

  disposer = observe(ShopStore.currentShop, 'id', () => {
    this.init();
  });

  deburrLoadFleetCosts = null;

  loadDataForPartRepeatVisits = async () => {
    try {
      this.setState({ isLoadingPartRepeatVisits: true });
      const { limit, filter, dateRange } = this.state;
      let [startDate, endDate] = dateRange;

      const response = await ShopStore.getShopDataForPartRepeatVisits(
        ShopStore.currentShop.id,
        limit,
        filter,
        startDate,
        endDate
      );
      const data = get(response, 'data', []);

      this.setState({ rawData: data });

      // Process the data
      const processedData = [];
      const yAxisCategories = data.map((item) => item.vmrsDescription);

      data.forEach((item, index) => {
        processedData.push({
          x: index,
          y: item.visit_freq,
          part_name: item.vmrsDescription,
        });
      });

      this.setState({
        dataForPartRepeatVisits: processedData,
        part_names: yAxisCategories,
      });
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.message,
      });
      this.setState({ dataForPartRepeatVisits: [] });
    } finally {
      this.setState({ isLoadingPartRepeatVisits: false });
    }
  };

  init = async () => {
    this.loadDataForPartRepeatVisits();
  };

  handleFilterChange = (changedFields) => {
    this.setState(changedFields);

    // call deburrLoadAssetDuration
    if (changedFields.limit || changedFields.dateRange) {
      if (this.deburrLoadFleetCosts) {
        clearTimeout(this.deburrLoadFleetCosts);
      }
      this.deburrLoadFleetCosts = setTimeout(() => {
        this.loadDataForPartRepeatVisits();
      }, 500);
    }
  };

  handleResetFilters = () => {
    this.setState(
      {
        limit: 20,
        dateRange: [dayjs().subtract(30, 'day'), dayjs()],
        filter: {
          vendor: [],
          make: [],
          model: [],
          year: [],
          carIds: [],
          assetType: [],
          VMRSSysCode: [],
        },
      },
      () => {
        this.loadDataForPartRepeatVisits();
      }
    );
  };

  handleApplyFilters = () => {
    this.init();
  };

  handleExportToCSV = async () => {
    // Show a loading notification
    const notificationId = notifications.loading(
      'Exporting VMRS Visits data to CSV... You can keep using the app while this is happening.'
    );

    try {
      const { rawData } = this.state;

      const csvHeaders = [
        'VMRS System Code',
        'Part Description',
        'Number of Visits',
      ];

      const csvData = rawData.map((item) => [
        item.vmrs_sys_code,
        item.vmrs_sys_description,
        item.visit_freq,
      ]);

      const fileName = `VMRS Visits Data - ${new Date().toLocaleDateString()}`;
      downloadCSV([csvHeaders, ...csvData], fileName, false);

      // Update notification to success
      notifications.success('VMRS Visits data exported successfully!', {
        id: notificationId,
      });
    } catch (error) {
      // Update notification to error
      const errorMessage = error?.message || 'Unknown error occurred';
      notifications.error(`Failed to export CSV: ${errorMessage}`, {
        id: notificationId,
      });
    }
  };

  render() {
    const { limit, isLoadingPartRepeatVisits, filter, dateRange } = this.state;

    const PartRepeatVisitsChartOptions = {
      chart: {
        type: 'column',
      },
      title: {
        text: null,
      },
      xAxis: {
        categories: this.state.part_names,
        crosshair: true,
        title: {
          text: 'VMRS Description',
        },
      },
      yAxis: {
        min: 0,
        title: {
          text: 'Number of Visits',
        },
      },
      plotOptions: {
        series: {
          cursor: 'pointer',
          // point: { commented out because clicking on bar shouldn't bring user anywhere
          //   events: {
          //     click: function () {
          //       // Construct the URL dynamically
          //       const part_name = this.options.part_name;
          //       const customUrl = `http://dashboard.pitstopconnect.com/car/${part_name}`;
          //       // window.location.href = customUrl;
          //       window.open(customUrl, '_blank');
          //     }
          //   }
          // },
          dataLabels: {
            enabled: true,
          },
        },
      },
      series: [
        {
          name: 'Visits',
          data: this.state.dataForPartRepeatVisits.map((item) => ({
            y: item.y,
            part_name: item.part_name, // Include part name in the data point
          })), // Adjusted field name
          color: '#183cbc',
        },
      ],
    };

    return (
      <Fragment>
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <h1>VMRS Visit Frequency</h1>
          </Col>
        </Row>
        <DealershipChooser />
        <FiltersFormWithVVA
          filter={{
            filter: filter,
          }}
          dateRange={dateRange}
          limit={limit}
          hideStatusFilter={true}
          onChange={this.handleFilterChange}
          onReset={this.handleResetFilters}
          onApply={this.handleApplyFilters}
        />
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <Spin spinning={isLoadingPartRepeatVisits}>
              <Card>
                <Row gutter={[8, 8]}>
                  <Col span={24}>
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={PartRepeatVisitsChartOptions}
                    />
                  </Col>
                </Row>
              </Card>
            </Spin>
          </Col>
        </Row>
      </Fragment>
    );
  }
}

export default withRouter(observer(PartRepeatVisitsPage));
